import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User,
  Building,
  Share2,
  Camera,
  CheckCircle2,
  AlertCircle,
  Upload,
  X,
  Linkedin,
  Instagram
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const subespecialidades = [
  'Oftalmología General',
  'Baja Visión',
  'Catarata',
  'Córnea',
  'Defensa Salud Visual',
  'Estrabismo',
  'Glaucoma',
  'Gremialismo',
  'Infectología',
  'Neurooftalmología',
  'Oculoplástica y vías lagrimales',
  'Órbita',
  'Oftalmopediatría',
  'Refracción',
  'Refractiva',
  'Praxis Médica',
  'Presbicia',
  'Retina',
  'Superficie Ocular',
  'Uveítis',
  'Otra'
];

const provincias = [
  'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes',
  'Entre Ríos', 'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones',
  'Neuquén', 'Río Negro', 'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe',
  'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
];

const initialFormData = {
  nombre_apellido: '',
  breve_cv: '',
  foto_perfil: null,
  
  // Dirección Principal
  direccion_principal: {
    nombre_establecimiento: '',
    telefono_pacientes: '',
    email_pacientes: '',
    calle: '',
    numero: '',
    piso_depto: '',
    codigo_postal: '',
    localidad: '',
    provincia: ''
  },
  
  // Dirección Secundaria
  direccion_secundaria: {
    nombre_establecimiento: '',
    telefono_pacientes: '',
    email_pacientes: '',
    calle: '',
    numero: '',
    piso_depto: '',
    codigo_postal: '',
    localidad: '',
    provincia: ''
  },
  
  // Redes Sociales
  linkedin: '',
  instagram: '',
  
  // Subespecialidades
  subespecialidad_principal: '',
  subespecialidad_2: '',
  subespecialidad_3: '',
  
  // Autorizaciones
  autoriza_publicacion: false,
  cuota_al_dia: false
};

const GuiaProfesionales = () => {
  const { user, getAuthHeaders } = useAuth();
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [existingGuia, setExistingGuia] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showSecondaryAddress, setShowSecondaryAddress] = useState(false);

  useEffect(() => {
    const checkExistingGuia = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/guia-profesionales/me`, {
          headers: getAuthHeaders()
        });
        if (response.data) {
          setExistingGuia(response.data);
        }
      } catch (error) {
        // No existing guia
      } finally {
        setCheckingExisting(false);
      }
    };
    checkExistingGuia();
  }, [getAuthHeaders]);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        nombre_apellido: `${user.nombre} ${user.apellido}`
      }));
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('La foto no puede superar los 2MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, foto_perfil: reader.result }));
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setFormData(prev => ({ ...prev, foto_perfil: null }));
    setPhotoPreview(null);
  };

  const validateForm = () => {
    if (!formData.nombre_apellido) {
      toast.error('Ingrese su nombre y apellido');
      return false;
    }
    if (!formData.breve_cv || formData.breve_cv.length < 50) {
      toast.error('El CV debe tener al menos 50 caracteres');
      return false;
    }
    if (!formData.direccion_principal.nombre_establecimiento) {
      toast.error('Ingrese el nombre del establecimiento principal');
      return false;
    }
    if (!formData.direccion_principal.telefono_pacientes) {
      toast.error('Ingrese el teléfono para pacientes');
      return false;
    }
    if (!formData.direccion_principal.calle || !formData.direccion_principal.numero) {
      toast.error('Complete la dirección principal');
      return false;
    }
    if (!formData.direccion_principal.codigo_postal || !formData.direccion_principal.localidad || !formData.direccion_principal.provincia) {
      toast.error('Complete todos los campos de ubicación');
      return false;
    }
    if (!formData.subespecialidad_principal) {
      toast.error('Seleccione su subespecialidad principal');
      return false;
    }
    if (!formData.autoriza_publicacion) {
      toast.error('Debe autorizar la publicación de sus datos');
      return false;
    }
    if (!formData.cuota_al_dia) {
      toast.error('Debe confirmar que su cuota está al día');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Clean up empty secondary address
      const dataToSend = {
        ...formData,
        direccion_secundaria: showSecondaryAddress && formData.direccion_secundaria.nombre_establecimiento 
          ? formData.direccion_secundaria 
          : null
      };
      
      await axios.post(`${API_URL}/api/guia-profesionales`, dataToSend, {
        headers: getAuthHeaders()
      });
      toast.success('Solicitud enviada correctamente');
      setExistingGuia({ estado: 'pendiente', datos: formData });
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (checkingExisting) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  if (existingGuia) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="guia-profesionales-existing">
        <Card className="cao-card">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              {existingGuia.estado === 'pendiente' ? (
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              ) : existingGuia.estado === 'aprobado' ? (
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              ) : (
                <AlertCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <CardTitle className="text-2xl text-[#1E3876]">
              Solicitud de Guía de Profesionales
            </CardTitle>
            <CardDescription>
              Ya tiene una solicitud registrada
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Estado:</span>
                <Badge className={
                  existingGuia.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                  existingGuia.estado === 'aprobado' ? 'bg-green-100 text-green-700' :
                  'bg-red-100 text-red-700'
                }>
                  {existingGuia.estado.charAt(0).toUpperCase() + existingGuia.estado.slice(1)}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Nombre:</span>
                <span className="font-medium">{existingGuia.datos?.nombre_apellido}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Subespecialidad:</span>
                <span className="font-medium text-right max-w-[200px]">
                  {existingGuia.datos?.subespecialidad_principal}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Establecimiento:</span>
                <span className="font-medium text-right max-w-[200px]">
                  {existingGuia.datos?.direccion_principal?.nombre_establecimiento}
                </span>
              </div>
            </div>
            
            {existingGuia.estado === 'pendiente' && (
              <p className="text-center text-gray-600">
                Su solicitud está siendo revisada. Una vez aprobada, sus datos aparecerán 
                en la Guía de Profesionales disponible en www.oftalmologos.org.ar/guiaprofesionales
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto" data-testid="guia-profesionales-form">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#1E3876] mb-2">Solicitud Guía de Profesionales</h1>
        <p className="text-gray-600">
          Complete el siguiente formulario para publicar sus datos en la Guía de Profesionales del CAO. 
          La guía está disponible en www.oftalmologos.org.ar/guiaprofesionales
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Datos Personales */}
        <Card className="cao-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1E3876]">
              <User className="w-5 h-5" />
              Datos Personales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Foto de perfil */}
              <div className="flex flex-col items-center">
                <Label className="cao-label mb-2">Foto de Perfil (4x4)</Label>
                {photoPreview ? (
                  <div className="relative">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-32 h-32 rounded-full object-cover border-4 border-[#2853B9]"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="w-32 h-32 rounded-full border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-[#3DA1E5] transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoChange}
                      data-testid="foto-perfil-input"
                    />
                    <Camera className="w-8 h-8 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Subir foto</span>
                  </label>
                )}
              </div>

              {/* Nombre y CV */}
              <div className="md:col-span-2 space-y-4">
                <div>
                  <Label className="cao-label">Nombre y Apellido *</Label>
                  <Input
                    className="cao-input"
                    value={formData.nombre_apellido}
                    onChange={(e) => handleInputChange('nombre_apellido', e.target.value)}
                    data-testid="nombre-apellido-input"
                  />
                </div>
                <div>
                  <Label className="cao-label">Breve CV * (máx. 1000 caracteres)</Label>
                  <Textarea
                    className="min-h-[120px] rounded-lg border-gray-200 focus:ring-2 focus:ring-[#3DA1E5]"
                    placeholder="Describa brevemente su trayectoria profesional, formación y experiencia..."
                    maxLength={1000}
                    value={formData.breve_cv}
                    onChange={(e) => handleInputChange('breve_cv', e.target.value)}
                    data-testid="breve-cv-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {formData.breve_cv.length}/1000 caracteres
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dirección Principal */}
        <Card className="cao-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1E3876]">
              <Building className="w-5 h-5" />
              Dirección Principal *
            </CardTitle>
            <CardDescription>Complete su lugar de atención principal</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="cao-label">Nombre de Hospital/Clínica/Consultorio *</Label>
              <Input
                className="cao-input"
                placeholder="Ej: Consultorio Dr. García"
                value={formData.direccion_principal.nombre_establecimiento}
                onChange={(e) => handleAddressChange('direccion_principal', 'nombre_establecimiento', e.target.value)}
                data-testid="establecimiento-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="cao-label">Teléfono para pacientes *</Label>
                <Input
                  className="cao-input"
                  placeholder="+54 11 1234-5678"
                  value={formData.direccion_principal.telefono_pacientes}
                  onChange={(e) => handleAddressChange('direccion_principal', 'telefono_pacientes', e.target.value)}
                  data-testid="telefono-pacientes-input"
                />
              </div>
              <div>
                <Label className="cao-label">Email para pacientes</Label>
                <Input
                  type="email"
                  className="cao-input"
                  placeholder="consultorio@ejemplo.com"
                  value={formData.direccion_principal.email_pacientes}
                  onChange={(e) => handleAddressChange('direccion_principal', 'email_pacientes', e.target.value)}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label className="cao-label">Calle *</Label>
                <Input
                  className="cao-input"
                  placeholder="Av. Corrientes"
                  value={formData.direccion_principal.calle}
                  onChange={(e) => handleAddressChange('direccion_principal', 'calle', e.target.value)}
                  data-testid="calle-input"
                />
              </div>
              <div>
                <Label className="cao-label">Número *</Label>
                <Input
                  className="cao-input"
                  placeholder="1234"
                  value={formData.direccion_principal.numero}
                  onChange={(e) => handleAddressChange('direccion_principal', 'numero', e.target.value)}
                />
              </div>
              <div>
                <Label className="cao-label">Piso/Depto</Label>
                <Input
                  className="cao-input"
                  placeholder="3° B"
                  value={formData.direccion_principal.piso_depto}
                  onChange={(e) => handleAddressChange('direccion_principal', 'piso_depto', e.target.value)}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label className="cao-label">Código Postal *</Label>
                <Input
                  className="cao-input"
                  placeholder="1043"
                  value={formData.direccion_principal.codigo_postal}
                  onChange={(e) => handleAddressChange('direccion_principal', 'codigo_postal', e.target.value)}
                />
              </div>
              <div>
                <Label className="cao-label">Localidad *</Label>
                <Input
                  className="cao-input"
                  placeholder="CABA"
                  value={formData.direccion_principal.localidad}
                  onChange={(e) => handleAddressChange('direccion_principal', 'localidad', e.target.value)}
                />
              </div>
              <div>
                <Label className="cao-label">Provincia *</Label>
                <Select
                  value={formData.direccion_principal.provincia}
                  onValueChange={(value) => handleAddressChange('direccion_principal', 'provincia', value)}
                >
                  <SelectTrigger className="cao-input">
                    <SelectValue placeholder="Seleccione" />
                  </SelectTrigger>
                  <SelectContent>
                    {provincias.map((prov) => (
                      <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dirección Secundaria (opcional) */}
        <Card className="cao-card mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-[#1E3876]">
                  <Building className="w-5 h-5" />
                  Dirección Secundaria
                </CardTitle>
                <CardDescription>Esta sección es opcional</CardDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowSecondaryAddress(!showSecondaryAddress)}
              >
                {showSecondaryAddress ? 'Ocultar' : 'Agregar'}
              </Button>
            </div>
          </CardHeader>
          {showSecondaryAddress && (
            <CardContent className="space-y-4">
              <div>
                <Label className="cao-label">Nombre de Hospital/Clínica/Consultorio</Label>
                <Input
                  className="cao-input"
                  value={formData.direccion_secundaria.nombre_establecimiento}
                  onChange={(e) => handleAddressChange('direccion_secundaria', 'nombre_establecimiento', e.target.value)}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="cao-label">Teléfono para pacientes</Label>
                  <Input
                    className="cao-input"
                    value={formData.direccion_secundaria.telefono_pacientes}
                    onChange={(e) => handleAddressChange('direccion_secundaria', 'telefono_pacientes', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="cao-label">Email para pacientes</Label>
                  <Input
                    type="email"
                    className="cao-input"
                    value={formData.direccion_secundaria.email_pacientes}
                    onChange={(e) => handleAddressChange('direccion_secundaria', 'email_pacientes', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Label className="cao-label">Calle</Label>
                  <Input
                    className="cao-input"
                    value={formData.direccion_secundaria.calle}
                    onChange={(e) => handleAddressChange('direccion_secundaria', 'calle', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="cao-label">Número</Label>
                  <Input
                    className="cao-input"
                    value={formData.direccion_secundaria.numero}
                    onChange={(e) => handleAddressChange('direccion_secundaria', 'numero', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="cao-label">Piso/Depto</Label>
                  <Input
                    className="cao-input"
                    value={formData.direccion_secundaria.piso_depto}
                    onChange={(e) => handleAddressChange('direccion_secundaria', 'piso_depto', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="cao-label">Código Postal</Label>
                  <Input
                    className="cao-input"
                    value={formData.direccion_secundaria.codigo_postal}
                    onChange={(e) => handleAddressChange('direccion_secundaria', 'codigo_postal', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="cao-label">Localidad</Label>
                  <Input
                    className="cao-input"
                    value={formData.direccion_secundaria.localidad}
                    onChange={(e) => handleAddressChange('direccion_secundaria', 'localidad', e.target.value)}
                  />
                </div>
                <div>
                  <Label className="cao-label">Provincia</Label>
                  <Select
                    value={formData.direccion_secundaria.provincia}
                    onValueChange={(value) => handleAddressChange('direccion_secundaria', 'provincia', value)}
                  >
                    <SelectTrigger className="cao-input">
                      <SelectValue placeholder="Seleccione" />
                    </SelectTrigger>
                    <SelectContent>
                      {provincias.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Redes Sociales */}
        <Card className="cao-card mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1E3876]">
              <Share2 className="w-5 h-5" />
              Redes Sociales Profesionales
            </CardTitle>
            <CardDescription>Opcional - Indique su usuario</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="cao-label flex items-center gap-2">
                  <Linkedin className="w-4 h-4" />
                  LinkedIn
                </Label>
                <Input
                  className="cao-input"
                  placeholder="usuario-linkedin"
                  value={formData.linkedin}
                  onChange={(e) => handleInputChange('linkedin', e.target.value)}
                />
              </div>
              <div>
                <Label className="cao-label flex items-center gap-2">
                  <Instagram className="w-4 h-4" />
                  Instagram
                </Label>
                <Input
                  className="cao-input"
                  placeholder="@usuario"
                  value={formData.instagram}
                  onChange={(e) => handleInputChange('instagram', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subespecialidades */}
        <Card className="cao-card mb-6">
          <CardHeader>
            <CardTitle className="text-[#1E3876]">Subespecialidades</CardTitle>
            <CardDescription>Seleccione al menos una subespecialidad</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="cao-label">Subespecialidad Principal *</Label>
              <Select
                value={formData.subespecialidad_principal}
                onValueChange={(value) => handleInputChange('subespecialidad_principal', value)}
              >
                <SelectTrigger className="cao-input" data-testid="subespecialidad-select">
                  <SelectValue placeholder="Seleccione subespecialidad" />
                </SelectTrigger>
                <SelectContent>
                  {subespecialidades.map((sub) => (
                    <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="cao-label">Subespecialidad 2</Label>
                <Select
                  value={formData.subespecialidad_2}
                  onValueChange={(value) => handleInputChange('subespecialidad_2', value)}
                >
                  <SelectTrigger className="cao-input">
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent>
                    {subespecialidades.map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="cao-label">Subespecialidad 3</Label>
                <Select
                  value={formData.subespecialidad_3}
                  onValueChange={(value) => handleInputChange('subespecialidad_3', value)}
                >
                  <SelectTrigger className="cao-input">
                    <SelectValue placeholder="Opcional" />
                  </SelectTrigger>
                  <SelectContent>
                    {subespecialidades.map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Autorización */}
        <Card className="cao-card mb-6">
          <CardHeader>
            <CardTitle className="text-[#1E3876]">Autorización para Publicación</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg text-sm text-gray-700 space-y-2">
              <p>
                Autorizo al Consejo Argentino de Oftalmología (CAO) a publicar los datos que proporcione 
                en este formulario (nombre, matrícula, dirección del consultorio, localidad, provincia, 
                datos de contacto, especialidad y subespecialidad, y geolocalización) en la Guía de 
                Profesionales disponible en la web pública del CAO.
              </p>
              <p>
                Declaro que la entrega de estos datos es voluntaria y que puedo revocar esta autorización 
                o solicitar la actualización o eliminación de mis datos en cualquier momento escribiendo a 
                gestiones@oftalmologos.org.ar
              </p>
              <p className="font-medium">
                He sido informado/a de mis derechos establecidos en la Ley N.º 25.326 de Protección de 
                Datos Personales y acepto el tratamiento y publicación de mis datos conforme a la misma.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <Checkbox
                  id="autoriza"
                  checked={formData.autoriza_publicacion}
                  onCheckedChange={(checked) => handleInputChange('autoriza_publicacion', checked)}
                  data-testid="autoriza-publicacion-checkbox"
                />
                <Label htmlFor="autoriza" className="font-normal cursor-pointer">
                  Autorizo a publicar mis datos en la Guía de Profesionales *
                </Label>
              </div>

              <div className="flex items-start gap-3 p-3 border rounded-lg">
                <Checkbox
                  id="cuota"
                  checked={formData.cuota_al_dia}
                  onCheckedChange={(checked) => handleInputChange('cuota_al_dia', checked)}
                  data-testid="cuota-al-dia-checkbox"
                />
                <Label htmlFor="cuota" className="font-normal cursor-pointer">
                  Tengo mi cuota al día y/o mis datos de tarjeta están vigentes *
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-6 font-semibold"
            data-testid="enviar-guia-btn"
          >
            {loading ? (
              <>
                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></span>
                Enviando...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Enviar Solicitud
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default GuiaProfesionales;
